import { AutoComplete, Button, Flex, Input, Radio, Space, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { EMAIL_LABELS, PHONE_LABELS } from '@crm/shared';
import type { ContactEmailItem, ContactPhoneItem } from '@crm/shared';

type Kind = 'email' | 'phone';
/** One row of the editor; `value` is the address or number. */
export interface ChannelRow {
  value: string;
  label: string | null;
  isPrimary: boolean;
}

const LABELS: Record<Kind, readonly string[]> = { email: EMAIL_LABELS, phone: PHONE_LABELS };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailRows = (list: ContactEmailItem[] | undefined): ChannelRow[] => (list ?? []).map((e) => ({ value: e.email, label: e.label, isPrimary: e.isPrimary }));
export const phoneRows = (list: ContactPhoneItem[] | undefined): ChannelRow[] => (list ?? []).map((p) => ({ value: p.number, label: p.label, isPrimary: p.isPrimary }));
const filled = (rows: ChannelRow[] | undefined) => (rows ?? []).filter((r) => r.value.trim());
export const toEmailsPayload = (rows: ChannelRow[] | undefined) => filled(rows).map((r) => ({ email: r.value.trim(), label: r.label?.trim() || null, isPrimary: r.isPrimary }));
export const toPhonesPayload = (rows: ChannelRow[] | undefined) => filled(rows).map((r) => ({ number: r.value.trim(), label: r.label?.trim() || null, isPrimary: r.isPrimary }));

/** Form rule: every filled-in address must look like an email. */
export const emailRowsRule = {
  validator: async (_: unknown, rows: ChannelRow[] | undefined) => {
    const bad = filled(rows).find((r) => !EMAIL_RE.test(r.value.trim()));
    if (bad) throw new Error(`“${bad.value}” is not a valid email address`);
  },
};

/** Editor for a contact's email addresses or phone numbers: a label, the value and a primary choice per row. */
export function ChannelsInput({ kind, value, onChange }: { kind: Kind; value?: ChannelRow[]; onChange?: (rows: ChannelRow[]) => void }) {
  const rows = value?.length ? value : [{ value: '', label: null, isPrimary: true }];
  const set = (next: ChannelRow[]) => onChange?.(next);
  const patch = (i: number, p: Partial<ChannelRow>) => set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const primaryIndex = Math.max(0, rows.findIndex((r) => r.isPrimary));
  const what = kind === 'email' ? 'email' : 'phone number';
  return (
    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AutoComplete
            aria-label={`Label for ${what} ${i + 1}`}
            placeholder="Label"
            value={r.label ?? ''}
            onChange={(v: string) => patch(i, { label: v })}
            options={LABELS[kind].map((l) => ({ value: l }))}
            filterOption={false}
            maxLength={40}
            style={{ width: 120, flex: 'none' }}
          />
          <Input
            aria-label={`${kind === 'email' ? 'Email' : 'Phone number'} ${i + 1}`}
            type={kind === 'email' ? 'email' : 'tel'}
            placeholder={kind === 'email' ? 'name@example.com' : '+1 555 010 2000'}
            value={r.value}
            onChange={(e) => patch(i, { value: e.target.value })}
            maxLength={kind === 'email' ? 254 : 60}
          />
          <Tooltip title={rows.length > 1 ? `Primary ${what}` : undefined}>
            <Radio
              aria-label={`Make ${what} ${i + 1} primary`}
              checked={i === primaryIndex}
              disabled={rows.length < 2}
              onChange={() => set(rows.map((x, j) => ({ ...x, isPrimary: j === i })))}
            >
              <span style={{ fontSize: 12 }}>Primary</span>
            </Radio>
          </Tooltip>
          <Button
            type="text"
            aria-label={`Remove ${what} ${i + 1}`}
            icon={<DeleteOutlined />}
            disabled={rows.length === 1 && !r.value && !r.label}
            onClick={() => {
              const next = rows.filter((_, j) => j !== i);
              if (i === primaryIndex && next.length) next[0] = { ...next[0], isPrimary: true };
              set(next);
            }}
          />
        </div>
      ))}
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => set([...rows, { value: '', label: null, isPrimary: false }])} disabled={rows.length >= 20}>
        Add {what}
      </Button>
    </Space>
  );
}

/** Read-only list for profile pages: each value with its label, primary first. */
export function ChannelList({ kind, items }: { kind: Kind; items: ChannelRow[] }) {
  if (!items.length) return null;
  return (
    <Flex wrap gap={6}>
      {items.map((r) => (
        <Tag key={r.value} color={r.isPrimary && items.length > 1 ? 'blue' : undefined} style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', minWidth: 0 }}>
          {/* plain CSS truncation: antd's measured ellipsis can't shrink inside a flex row */}
          <span title={r.value} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {kind === 'email' ? r.value : <a href={`tel:${r.value.replace(/[^\d+]/g, '')}`}>{r.value}</a>}
          </span>
          <Typography.Text copyable={{ text: r.value }} style={{ fontSize: 'inherit', color: 'inherit', flex: 'none', marginInlineStart: -4 }} />
          {(r.label || (r.isPrimary && items.length > 1)) && (
            <span style={{ opacity: 0.65, whiteSpace: 'nowrap', flex: 'none' }}>
              {[r.label, r.isPrimary && items.length > 1 ? 'Primary' : null].filter(Boolean).join(' · ')}
            </span>
          )}
        </Tag>
      ))}
    </Flex>
  );
}
